import {
  Phone,
  Pencil,
  Trash2,
  Clock,
  CheckCircle2,
  Circle,
  MessageCircle,
  BellOff,
  UserX,
  Send,
} from "lucide-react";
import { formatFecha, formatHora, formatPrecio, whatsappLink } from "@/lib/agenda";
import { RiskDot } from "@/components/RiskDot";

export const AppointmentCard = ({
  appt,
  therapists = [],
  risk,
  onEdit,
  onDelete,
  onTogglePayment,
  onToggleConfirmed,
  onToggleNoShow,
  onSendReminder,
}) => {
  const paid = appt.payment_status === "pagado";
  const confirmed = !!appt.reminder_confirmed;
  const noShow = !!appt.no_show;
  const sent = !!appt.reminder_sent;
  const therapist = therapists.find((t) => t.id === appt.therapistId);

  return (
    <div
      className="bg-white border border-[#EAE5DF] rounded-2xl shadow-[0_6px_24px_-12px_rgba(46,42,143,0.10)] hover:shadow-[0_14px_32px_-12px_rgba(46,42,143,0.18)] hover:-translate-y-0.5 transition-all duration-300 p-5"
      data-testid={`appointment-card-${appt.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <RiskDot risk={risk} />
            <h3 className="font-heading text-lg font-semibold text-slate-900 truncate">
              {appt.client_name}
            </h3>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {formatFecha(appt.appointment_datetime)} · {formatHora(appt.appointment_datetime)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Phone className="h-3.5 w-3.5" />
              {appt.client_phone}
            </span>
          </div>
          {therapist && (
            <span className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium" style={{ color: therapist.color }}>
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: therapist.color }} />
              {therapist.name}
            </span>
          )}
        </div>
        <span className="font-heading text-lg font-bold text-slate-900 shrink-0">
          {formatPrecio(appt.price)}
        </span>
      </div>

      {appt.notes ? (
        <p className="mt-3 text-sm text-slate-600 bg-slate-50 rounded-lg px-3 py-2">{appt.notes}</p>
      ) : null}

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100 gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            onClick={onTogglePayment}
            data-testid={`toggle-payment-${appt.id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              paid ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"
            }`}
          >
            {paid ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
            {paid ? "Pagado" : "Pendiente"}
          </button>

          <button
            onClick={onToggleConfirmed}
            data-testid={`toggle-confirmed-${appt.id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              confirmed ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-slate-50 border-slate-200 text-slate-500"
            }`}
          >
            {confirmed ? <CheckCircle2 className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
            {confirmed ? "Confirmada" : "Sin confirmar"}
          </button>

          <button
            onClick={onToggleNoShow}
            data-testid={`toggle-noshow-${appt.id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              noShow ? "bg-red-50 border-red-200 text-red-700" : "bg-slate-50 border-slate-200 text-slate-500"
            }`}
          >
            <UserX className="h-3.5 w-3.5" />
            {noShow ? "No asistió" : "Asistió"}
          </button>

          {sent && (
            <span
              data-testid={`reminder-sent-${appt.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border bg-emerald-50 border-emerald-200 text-emerald-700"
            >
              <Send className="h-3.5 w-3.5" />
              Recordatorio enviado
            </span>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => onSendReminder && onSendReminder(appt)}
            data-testid={`whatsapp-${appt.id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              sent ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "text-white"
            }`}
            style={sent ? {} : { backgroundColor: "#0E9F6E" }}
            title="Preparar recordatorio por WhatsApp"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            {sent ? "Reenviar" : "Enviar recordatorio"}
          </button>
          <button onClick={onEdit} data-testid={`edit-${appt.id}`} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors" aria-label="Editar cita">
            <Pencil className="h-4 w-4" />
          </button>
          <button onClick={onDelete} data-testid={`delete-${appt.id}`} className="p-2 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors" aria-label="Eliminar cita">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
